import { Component, DestroyRef, inject, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgIf } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { StorageService } from '../../../../../core/services/storage/storage.service';
import { UserService } from '../../../../../core/services/user/user.service';
import { User } from '../../../../../core/models/user/user';
import { UserParameterService } from '../../../../../core/services/userParameter/user-parameter.service';
import { AlertService } from '../../../../../core/services/alert/alert.service';
import { PaypalService } from '../../../../../core/services/register/servicesPaypal/paypal/paypal.service';
import { AuthService } from '../../../../../core/services/auth/auth.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
@Component({
    selector: 'app-unsub-abonnement',
    templateUrl: './unsub-abonnement.component.html',
    styleUrls: ['./unsub-abonnement.component.scss'],
    imports: [FormsModule, NgIf, TranslateModule]
})
export class UnsubAbonnementComponent implements OnDestroy {
  protected modal = inject(NgbActiveModal);
  private storageService = inject(StorageService);
  private userService = inject(UserService);
  private userParameterService = inject(UserParameterService);
  private paypalService = inject(PaypalService);
  private alertService = inject(AlertService);
  private router = inject(Router);
  private authService = inject(AuthService);


  private destroyRef = inject(DestroyRef);

  protected user: User | undefined = this.storageService.getUser();
  protected isWaitingForProfileUpdate = false;
  protected profileUpdateCheckInterval: any = null;
  protected autoLogoutTimeout: any = null;

  public confirm() {
    // Start waiting for profile update
    this.isWaitingForProfileUpdate = true;
    
    // Store initial user state for comparison
    const initialUser = { ...this.user };
    const initialProfile = initialUser?.profile?.name || '';
    const initialSubscriptions = initialUser?.subscriptions?.filter((s: any) => !s.deleted) || [];
    
    // Set up auto-logout timeout (2 minutes)
    this.setupAutoLogoutTimeout();

    const changeProfileCallback = {
      next: (response: any) => {
        // Check if profile is already USER in the response
        const responseProfile = response?.profile?.name || '';
        const responseProfileUpper = responseProfile.toUpperCase();
        
        if (responseProfileUpper === 'USER') {
          // Profile already updated, no need to poll
          this.stopProfileUpdateCheck();
          this.clearAutoLogoutTimeout();
          this.isWaitingForProfileUpdate = false;
          
          this.storageService.setUser(response);
          localStorage.removeItem('paypal_subscription_pending');
          localStorage.removeItem('paypal_expected_profile');
          window.dispatchEvent(new CustomEvent('userUpdated'));
          
          this.modal.dismiss(response);
          
          this.alertService.open({
            header: 'Confirmation',
            content: 'La version de votre compte a été modifiée. Votre compte est maintenant en version "Standard" et votre souscription a été annulée. Un email de confirmation vous a été envoyé.',
            contentType: 'text'
          });
        } else {
          // Start polling to wait for profile update
          this.waitForProfileUpdate(initialUser, initialProfile, initialSubscriptions);
        }
      },
      error: (err: any) => {
        this.stopProfileUpdateCheck();
        this.clearAutoLogoutTimeout();
        this.isWaitingForProfileUpdate = false;
        
        // The backend now handles PayPal cancellation failures gracefully
        // If we get here, it's a different type of error
        let errorMessage = 'Une erreur est survenue lors de l\'annulation de votre abonnement.';
        
        if (err?.error?.message) {
          errorMessage = err.error.message;
        }
        
        this.alertService.open({
          header: 'Erreur',
          content: errorMessage,
          contentType: 'text'
        });
      },
    };

    // Find subscription matching user's current profile (same logic as original Premium)
    const userProfile = this.user?.profile?.name;
    let subscriptionsToCheck: any[] = [];
    
    // Determine which subscription type to cancel based on user profile
    if (userProfile === 'PRO') {
      subscriptionsToCheck = this.user?.subscriptions?.filter((s) => s.type == 'PRO' && !s.deleted) || [];
    } else if (userProfile === 'ESSENTIEL') {
      subscriptionsToCheck = this.user?.subscriptions?.filter((s) => s.type == 'ESSENTIEL' && !s.deleted) || [];
    } else if (userProfile === 'PREMIUM' || userProfile === 'PREMIUM_OFFERT' || userProfile === 'PREMIUM_DECOUVERTE') {
      subscriptionsToCheck = this.user?.subscriptions?.filter((s) => s.type == 'PREMIUM' && !s.deleted) || [];
    } else {
      // Fallback: check all paid subscription types
      const paidSubscriptionTypes = ['PREMIUM', 'ESSENTIEL', 'PRO'];
      subscriptionsToCheck = this.user?.subscriptions?.filter((s) => 
        paidSubscriptionTypes.includes(s.type || '') && !s.deleted
      ) || [];
    }
    
    if (subscriptionsToCheck.length > 0) {
      // Filter to only keep subscriptions with valid PayPal IDs (starting with 'I-')
      // Valid PayPal subscription IDs typically start with 'I-' followed by alphanumeric characters
      const validPaypalSubscriptions = subscriptionsToCheck.filter(s => {
        const id = s.subscriptionId?.trim() || '';
        return id.startsWith('I-') && id.length > 2;
      });
      
      if (validPaypalSubscriptions.length > 0) {
        // Try each valid PayPal subscription until one works
        const tryCancelSubscription = (index: number) => {
          if (index >= validPaypalSubscriptions.length) {
            // All valid PayPal subscriptions failed, but backend should have updated profile anyway
            // Just update profile directly as fallback
            this.userParameterService
              .updateProfile({ newProfile: 'USER' })
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe(changeProfileCallback);
            return;
          }
          
          const subscriptionToCancel = validPaypalSubscriptions[index];
          const subscriptionId = subscriptionToCancel.subscriptionId?.trim();
          
          const errorCallback = {
            next: (response: any) => {
              // Success - use the success callback
              changeProfileCallback.next(response);
            },
            error: (err: any) => {
              // If it's a 403 (Forbidden) or 400 (Bad Request) error and there are more subscriptions, try the next one
              if ((err?.status === 403 || err?.status === 400) && index < validPaypalSubscriptions.length - 1) {
                tryCancelSubscription(index + 1);
              } else {
                // Last subscription or different error - backend should have updated profile anyway
                // But if not, update profile directly as fallback
                this.userParameterService
                  .updateProfile({ newProfile: 'USER' })
                  .pipe(takeUntilDestroyed(this.destroyRef))
                  .subscribe(changeProfileCallback);
              }
            }
          };
          
          this.paypalService
            .cancelPaypalSubscription(subscriptionId, "simple", this.user?.idUser)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(errorCallback);
        };
        
        // Start trying from the first valid PayPal subscription
        tryCancelSubscription(0);
        return;
      } else {
        // No valid PayPal subscriptions found, just update profile directly
      }
    }

    // Fallback: if no subscription found, just update profile
    this.userParameterService
      .updateProfile({ newProfile: 'USER' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(changeProfileCallback);
  }

  get isPremiumGiftedUser(): boolean {
    return this.userService.isPremiumGiftedUser(this.user);
  }

  get isPremiumDiscoveryUser(): boolean {
    return this.userService.isPremiumDiscoveryUser(this.user);
  }

  get isPremiumUser(): boolean {
    return this.userService.isPremiumUser(this.user);
  }

  get isProUser(): boolean {
    return this.userService.isProUser(this.user);
  }

  get isEssentielUser(): boolean {
    return this.userService.isEssentielUser(this.user);
  }

  private waitForProfileUpdate(initialUser: any, initialProfile: string, initialSubscriptions: any[]) {
    const maxAttempts = 24; // 2 minutes max (24 * 5 seconds)
    let attempts = 0;
    
    this.profileUpdateCheckInterval = setInterval(() => {
      attempts++;
      
      this.userService.getCurrentUser()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (updatedUser: any) => {
            const currentProfile = updatedUser?.profile?.name || '';
            const currentSubscriptions = updatedUser?.subscriptions?.filter((s: any) => !s.deleted) || [];
            const currentProfileUpper = currentProfile.toUpperCase();
            
            // Check if profile changed to USER
            const profileChangedToUser = currentProfileUpper === 'USER';
            
            // Check if subscriptions changed (deleted)
            const subscriptionsChanged = currentSubscriptions.length < initialSubscriptions.length ||
              currentSubscriptions.some((newSub: any) => 
                !initialSubscriptions.some((initSub: any) => initSub.subscriptionId === newSub.subscriptionId)
              );
            
            // Profile is updated if it's USER OR subscriptions changed OR max attempts reached
            const isUpdated = profileChangedToUser || 
              subscriptionsChanged ||
              attempts >= maxAttempts;
            
            if (isUpdated) {
              this.stopProfileUpdateCheck();
              this.clearAutoLogoutTimeout();
              this.isWaitingForProfileUpdate = false;
              
              // Update local user
              this.storageService.setUser(updatedUser);
              
              // Clear PayPal flags
              localStorage.removeItem('paypal_subscription_pending');
              localStorage.removeItem('paypal_expected_profile');
              
              // Trigger user update event
              window.dispatchEvent(new CustomEvent('userUpdated'));
              
              // Close modal and show success message
              this.modal.dismiss(updatedUser);
              
              this.alertService.open({
                header: 'Confirmation',
                content: 'La version de votre compte a été modifiée. Votre compte est maintenant en version "Standard" et votre souscription a été annulée. Un email de confirmation vous a été envoyé.',
                contentType: 'text'
              });
            }
          },
          error: () => {
            // Continue checking
            if (attempts >= maxAttempts) {
              this.stopProfileUpdateCheck();
              this.clearAutoLogoutTimeout();
              this.isWaitingForProfileUpdate = false;
            }
          }
        });
    }, 5000); // Check every 5 seconds
  }

  private stopProfileUpdateCheck() {
    if (this.profileUpdateCheckInterval) {
      clearInterval(this.profileUpdateCheckInterval);
      this.profileUpdateCheckInterval = null;
    }
  }

  private setupAutoLogoutTimeout() {
    // Clear any existing timeout
    this.clearAutoLogoutTimeout();
    
    // Set new timeout (2 minutes = 120000 ms)
    this.autoLogoutTimeout = setTimeout(() => {
      this.stopProfileUpdateCheck();
      this.isWaitingForProfileUpdate = false;
      
      // Clear PayPal flags
      localStorage.removeItem('paypal_subscription_pending');
      localStorage.removeItem('paypal_expected_profile');
      
      // Clear user data
      localStorage.removeItem('user');
      localStorage.removeItem('accountsSynchronizedOnce');
      localStorage.removeItem('dashboardGuideShown');
      localStorage.removeItem('dashboardwelcomeShown');
      
      // Logout via auth service
      this.authService.logout();
      
      // Close modal
      this.modal.dismiss('timeout');
      
      // Redirect to login with message
      this.router.navigate(['/authentication/login'], { 
        queryParams: { 
          disconnected: 'timeout',
          message: 'L\'annulation de votre abonnement a pris trop de temps. Veuillez vous reconnecter.'
        },
        replaceUrl: true
      });
    }, 120000); // 2 minutes
  }

  private clearAutoLogoutTimeout() {
    if (this.autoLogoutTimeout) {
      clearTimeout(this.autoLogoutTimeout);
      this.autoLogoutTimeout = null;
    }
  }

  ngOnDestroy() {
    this.stopProfileUpdateCheck();
    this.clearAutoLogoutTimeout();
  }
}

